<template>
  <OverflowMenuItem
    v-if="isOverflowMenu"
    class="selection-bar-menu-item"
    v-bind="attrs"
    :class="[attrs.class, { '_has-tooltip': showTooltip }]"
    @click="hideMenu"
  >
    <svg-icon v-if="hasIcon" :name="icon" kind="action" />
    <span>{{ label }}</span>
    <cv-tooltip
      v-if="showTooltip"
      :tip="tip"
      :alignment="validatedAlignment"
      :direction="validatedDirection"
    />
  </OverflowMenuItem>
  <li v-else class="selection-bar-menu-item">
    <tooltip-button
      v-if="showTooltip"
      v-bind="attrs"
      :tip="tip"
      :alignment="validatedAlignment"
      :direction="validatedDirection"
    >
      <svg-icon v-if="hasIcon" :name="icon" kind="action" />
      <span class="tooltip-button-label">{{ label }}</span>
    </tooltip-button>
    <button v-else class="button" v-bind="attrs" @click="onClick">
      <svg-icon v-if="hasIcon" :name="icon" kind="action" />
      <span>{{ label }}</span>
    </button>
  </li>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils.ts";
import { computed, useAttrs } from "vue";
import { useVm } from "@utils/vm.ts";

defineOptions({
  inheritAttrs: false,
});

// Props
interface Props {
  icon?: string;
  label?: string;
  showTooltip?: boolean;
  tip?: string;
  direction?: string;
  alignment?: string;
}

const {
  icon = "",
  label = "",
  showTooltip = false,
  tip = "",
  direction = "bottom",
  alignment = "center",
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "cv:click": [];
}>();

// Composables
const { vm } = useVm();
const attrs = useAttrs();

// Computed
const isOverflowMenu = computed(() => {
  return vm.$parent?.$options?.__name === "CvOverflowMenu";
});

const hasIcon = computed(() => {
  return icon && icon.length > 0;
});

const validatedDirection = computed(() => {
  return validateDirection(direction);
});

const validatedAlignment = computed(() => {
  return validateAlignment(alignment);
});

// Methods
const hideMenu = () => {
  const openedMenu = DOMUtils.elOf(document.body, ".bx--overflow-menu-options--open")[0];
  if (openedMenu) {
    DOMUtils.removeClass(openedMenu, "bx--overflow-menu-options--open");
  }
};

const onClick = () => {
  emit("cv:click");
};

const validateDirection = (value: string): string => {
  const validDirections = ["top", "right", "bottom", "left"];
  return validDirections.includes(value) ? value : "bottom";
};

const validateAlignment = (value: string): string => {
  const validAlignments = ["center", "start", "end"];
  return validAlignments.includes(value) ? value : "center";
};
</script>
